import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Check, Mountain, Trees, UtensilsCrossed, Heart, MapPin, Car } from 'lucide-react';
import { homestayInfo, rooms, experiences, reviews, usps, heroImages } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const iconMap = {
    Mountain,
    Trees,
    UtensilsCrossed,
    Heart,
    MapPin,
    Car
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello! I'd like to inquire about booking at White Orchid Homestay.");
    window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = `tel:${homestayInfo.phone}`;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image Slider */}
        <div className="absolute inset-0">
          {heroImages.map((img, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={img}
                alt={`White Orchid Homestay ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            </div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 mb-4">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium">{homestayInfo.rating} Rating • {homestayInfo.reviewCount}+ Reviews</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              {homestayInfo.name}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-light">
              {homestayInfo.tagline}
            </p>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
              Experience peaceful hospitality surrounded by pine forests with spectacular Mt. Kanchenjunga views in Kurseong
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                size="lg"
                onClick={handleWhatsAppClick}
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
              >
                Book on WhatsApp
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                onClick={handleCallClick}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30 text-lg px-8 py-6 rounded-lg backdrop-blur-md"
              >
                Call Now
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1F4D3A]">
              Your Peaceful Retreat in the Hills
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Nestled in the serene village of Chimney-Deorali near Dowhill School, White Orchid Homestay offers a perfect blend of comfort, nature, and authentic hospitality. Since {homestayInfo.established}, we've been welcoming guests to experience the tranquility of Kurseong's pine forests and the majestic beauty of Mt. Kanchenjunga.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              With our spotless rooms, delicious home-cooked food, and complimentary nature experiences, we provide a home away from home for couples, families, and adventure enthusiasts.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us - USPs */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1F4D3A] mb-4">
              Why Choose White Orchid
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover what makes us special
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {usps.map((usp, index) => {
              const Icon = iconMap[usp.icon];
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-[#A3B18A]/20 hover:border-[#1F4D3A]/30">
                  <CardContent className="p-6 space-y-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#1F4D3A] to-[#A3B18A] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#1F4D3A]">{usp.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{usp.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Rooms Preview */}
      <section className="py-20 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1F4D3A] mb-4">
              Comfortable Rooms
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose from our 4 spacious rooms, each designed for your comfort
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-8">
            {rooms.filter(room => room.featured).map((room) => (
              <Card key={room.id} className="overflow-hidden group hover:shadow-2xl transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={room.images[0]}
                    alt={room.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-[#D4A373] text-white px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </div>
                </div>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-[#1F4D3A] mb-2">{room.name}</h3>
                    <p className="text-[#A3B18A] font-medium">{room.view}</p>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p><strong>Capacity:</strong> {room.capacity} persons • {room.beds}</p>
                    <p><strong>Bathroom:</strong> {room.bathroom}</p>
                    <p><strong>Size:</strong> {room.size}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-lg font-semibold text-[#1F4D3A]">
                      Starting from ₹{room.tariff.withFood}/person
                    </p>
                    <p className="text-sm text-gray-500">Includes 4 meals</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center">
            <Link to="/rooms">
              <Button size="lg" className="bg-[#1F4D3A] hover:bg-[#2d6b52] text-white px-8">
                View All Rooms & Tariff
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Experiences Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1F4D3A] mb-4">
              Unique Experiences
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complimentary nature adventures included with your stay
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-8">
            {experiences.slice(0, 2).map((exp) => (
              <Card key={exp.id} className="overflow-hidden group hover:shadow-2xl transition-all duration-300">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={exp.images[0]}
                    alt={exp.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {exp.complimentary && (
                    <div className="absolute top-4 left-4 bg-[#25D366] text-white px-3 py-1 rounded-full text-sm font-medium">
                      Complimentary
                    </div>
                  )}
                </div>
                <CardContent className="p-6 space-y-3">
                  <h3 className="text-xl font-bold text-[#1F4D3A]">{exp.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{exp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center">
            <Link to="/experiences">
              <Button size="lg" variant="outline" className="border-[#1F4D3A] text-[#1F4D3A] hover:bg-[#1F4D3A] hover:text-white px-8">
                Explore All Experiences
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Food Highlight */}
      <section className="py-20 bg-gradient-to-br from-[#F7F5F0] to-[#A3B18A]/10">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-4xl md:text-5xl font-bold text-[#1F4D3A]">
                  Fresh, Home-Cooked Food
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Food is a highlight at White Orchid Homestay. We serve authentic, hygienic home-cooked meals prepared with fresh ingredients and lots of love.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-[#25D366] flex-shrink-0 mt-1" />
                    <p className="text-gray-700">Breakfast, Lunch, Evening Snacks, and Dinner included</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-[#25D366] flex-shrink-0 mt-1" />
                    <p className="text-gray-700">Fresh, locally sourced ingredients</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-[#25D366] flex-shrink-0 mt-1" />
                    <p className="text-gray-700">Special dietary requirements accommodated</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-[#25D366] flex-shrink-0 mt-1" />
                    <p className="text-gray-700">Outdoor dining and BBQ available on request</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1666251214795-a1296307d29c"
                  alt="Home cooked food"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
                <img
                  src="https://images.unsplash.com/photo-1546833999-b9f581a1996d"
                  alt="Traditional meal"
                  className="w-full h-64 object-cover rounded-lg shadow-lg mt-8"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#1F4D3A]/5 px-4 py-2 rounded-full mb-4">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              <span className="text-[#1F4D3A] font-semibold">{homestayInfo.rating} Average Rating</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1F4D3A] mb-4">
              Guest Reviews
            </h2>
            <p className="text-lg text-gray-600">
              {homestayInfo.reviewCount}+ happy guests on Google
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {reviews.map((review) => (
              <Card key={review.id} className="bg-[#F7F5F0] border-none">
                <CardContent className="p-6 space-y-4">
                  <div className="flex gap-1">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{review.text}</p>
                  <div className="pt-2 border-t border-gray-200">
                    <p className="font-semibold text-[#1F4D3A] text-sm">{review.author}</p>
                    <p className="text-xs text-gray-500">{review.date}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold">
              Ready to Experience Kurseong?
            </h2>
            <p className="text-xl text-white/90">
              Book your peaceful retreat at White Orchid Homestay today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                size="lg"
                onClick={handleWhatsAppClick}
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white text-lg px-8 py-6"
              >
                Book on WhatsApp
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                onClick={handleCallClick}
                variant="outline"
                className="bg-white text-[#1F4D3A] hover:bg-white/90 border-white text-lg px-8 py-6"
              >
                Call {homestayInfo.phone}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
